import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link } from 'react-router-dom';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

// Import utilities and components
import CreditConfirmModal from './CreditConfirmModal';
import PreviewModal from './PreviewModal';
import PasswordPromptModal from './PasswordPromptModal';
import {
  trackPDFUploadStarted,
  trackPDFUploadConfirmed,
  trackPDFConversionSuccess,
  trackPDFConversionError,
  trackUploadAbandonment,
  trackError
} from '../../utils/analytics';
import { getCloudFunctionURL } from '../../config/featureFlags';

// Firebase imports
// Storage imports removed - using HTTP upload only
import { doc, addDoc, collection, onSnapshot, getDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';
import { db, auth } from '../../firebase/config'; // storage removed - using HTTP upload

// Import Day 5 additions
import { useTransactions, TRANSACTION_STATUS } from '../../hooks/useTransactions';

// Import anonymous session utilities
import {
  generateFingerprint,
  markConversionCompleted
} from '../../utils/anonymousSession';

// Import download manager
import downloadManager from '../../utils/downloadManager';

const FileUpload = () => {
  // Authentication and credits
  const { currentUser: user } = useAuth();
  const { userCredits: credits, getUserCredits, updateUserCredits } = useAuth();
  const {
    anonymousUser,
    anonymousCredits,
    isAnonymous,
    updateAnonymousCredits
  } = useAuth();
  const refreshCredits = () => {
    if (user?.uid) {
      return getUserCredits(user.uid);
    }
  };

  // Day 5 addition - Transaction management
  const { updateTransactionStatus } = useTransactions();

  // Component state
  const [uploadState, setUploadState] = useState('idle'); // idle, confirming, uploading, complete, error, awaiting_preview, accepting_preview
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [uploadTask, setUploadTask] = useState(null);
  const [currentTransactionId, setCurrentTransactionId] = useState(null);
  const [pdfLibLoaded, setPdfLibLoaded] = useState(false);

  // Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTransactions, setPreviewTransactions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null); // Store year from CreditConfirmModal

  // Completed transaction data for download
  const [completedTransaction, setCompletedTransaction] = useState(null);

  // Page count state for credit validation
  const [selectedFilePageCount, setSelectedFilePageCount] = useState(null);

  // Password prompt modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // Store file temporarily while getting password

  // Refs for cleanup
  const uploadTaskRef = useRef(null);
  const transactionListenerRef = useRef(null); // Track Firestore listener
  const isMountedRef = useRef(true);
  const uploadStartTime = useRef(Date.now());

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (uploadTaskRef.current) {
        // Clear any pending poll timeouts
        if (uploadTaskRef.current.pollTimeouts) {
          uploadTaskRef.current.pollTimeouts.forEach(timeout => clearTimeout(timeout));
        }
        uploadTaskRef.current.cancel();
      }
      if (transactionListenerRef.current) {
        transactionListenerRef.current(); // Call unsubscribe function
        transactionListenerRef.current = null;
      }
    };
  }, []);

  // Load PDF.js library dynamically from CDN
  useEffect(() => {
    console.log('📚 Loading PDF.js library from CDN...');

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;
    script.onload = () => {
      console.log('✅ PDF.js library loaded from CDN');
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      setPdfLibLoaded(true);
    };
    script.onerror = () => {
      console.error('❌ Failed to load PDF.js library from CDN');
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get PDF page count using PDF.js (client-side validation)
  const getPdfPageCount = async (file, password = null) => {
    if (!window.pdfjsLib) {
      throw new Error('PDF library not loaded. Please refresh the page and try again.');
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = window.pdfjsLib.getDocument({
        data: arrayBuffer,
        password: password // Pass password if provided
      });
      const pdf = await loadingTask.promise;
      const pageCount = pdf.numPages;
      console.log(`📄 PDF Page Count: ${pageCount} pages${password ? ' (unlocked with password)' : ''}`);
      return pageCount;
    } catch (error) {
      // Handle password-protected PDFs - throw PasswordException to trigger password prompt
      if (error.name === 'PasswordException') {
        console.log('🔒 PDF is password-protected.');
        throw error; // Re-throw to trigger password prompt
      }

      // Handle incorrect password
      if (error.name === 'PasswordException' && password) {
        console.error('❌ Incorrect password provided');
        throw new Error('Incorrect password. Please try again.');
      }

      console.error('❌ Error reading PDF page count:', error);
      throw new Error('Failed to read PDF file. Please ensure it is a valid PDF.');
    }
  };

  // Reset form to initial state
  const resetForm = useCallback(() => {
    // Track abandonment if user resets during process
    if (selectedFile && uploadState !== 'complete' && uploadState !== 'idle') {
      trackUploadAbandonment(uploadState, {
        fileName: selectedFile.name,
        creditsRequired: selectedFilePageCount || 1
      });
    }

    setSelectedFile(null);
    setUploadProgress(0);
    setError(null);
    setUploadState('idle');
    setShowConfirmModal(false);
    setUploadTask(null);
    setCurrentTransactionId(null);
    setCompletedTransaction(null); // Clear completed transaction data
    setSelectedFilePageCount(null); // Clear page count
    uploadTaskRef.current = null;
  }, [selectedFile, uploadState, selectedFilePageCount]);

  // Handle password submission for password-protected PDFs
  const handlePasswordSubmit = useCallback(async (password) => {
    if (!pendingFile) {
      throw new Error('No file pending');
    }

    console.log('🔓 Attempting to unlock PDF with provided password...');

    try {
      // Try to count pages with the provided password
      const pageCount = await getPdfPageCount(pendingFile, password);

      console.log(`✅ PDF unlocked successfully! ${pageCount} pages detected.`);

      // Success - close password modal and proceed to confirmation
      setShowPasswordModal(false);
      setSelectedFilePageCount(pageCount);
      setPendingFile(null);

      // Continue with validation checks
      await continueFileValidation(pendingFile, pageCount, password);

    } catch (error) {
      console.error('❌ Failed to unlock PDF:', error);

      // Check if it's a password error
      if (error.name === 'PasswordException' || error.message?.includes('password')) {
        throw new Error('Incorrect password. Please try again.');
      }

      throw error;
    }
  }, [pendingFile]);

  // Continue file validation after password is provided (or for non-password-protected PDFs)
  const continueFileValidation = useCallback(async (file, pageCount, password = null) => {
    // Store password if provided
    if (password) {
      // We'll pass this to the backend later
      file.pdfPassword = password;
    }

    // 🔒 ANONYMOUS USER: Enforce 2-page limit
    if (isAnonymous && pageCount > 2) {
      setError(
        `Free trial is limited to 2-page PDFs. Your PDF has ${pageCount} pages. Please register to convert multi-page documents.`
      );
      setUploadState('error');

      // Track page limit error
      trackError({
        errorType: 'anonymous_page_limit_exceeded',
        errorMessage: `Anonymous user attempted to upload ${pageCount}-page PDF`,
        component: 'FileUpload',
        additionalContext: {
          fileName: file.name,
          pageCount: pageCount,
          userType: 'anonymous'
        }
      });
      return;
    }

    // 💳 REGISTERED USER: Validate sufficient credits
    if (!isAnonymous) {
      const currentCredits = credits || 0;
      const creditsRequired = pageCount;

      console.log(`💳 Credit check: User has ${currentCredits}, needs ${creditsRequired} for ${pageCount}-page PDF`);

      if (currentCredits < creditsRequired) {
        setError(
          `Insufficient credits. This PDF has ${pageCount} page${pageCount !== 1 ? 's' : ''} but you only have ${currentCredits} credit${currentCredits !== 1 ? 's' : ''}. Please purchase more credits to continue.`
        );
        setUploadState('error');

        // Track insufficient credits error
        trackError({
          errorType: 'insufficient_credits',
          errorMessage: `User has ${currentCredits} credits but needs ${creditsRequired}`,
          component: 'FileUpload',
          additionalContext: {
            fileName: file.name,
            pageCount: pageCount,
            creditsAvailable: currentCredits,
            creditsRequired: creditsRequired,
            userType: 'registered'
          }
        });
        return;
      }

      console.log(`✅ Credit validation passed: ${currentCredits} >= ${creditsRequired}`);
    }

    console.log(`✅ Page count validation passed: ${pageCount} page(s)`);

    // Track PDF upload started with accurate credit requirement
    trackPDFUploadStarted({
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type || 'application/pdf',
      creditsRequired: pageCount,
      pageCount: pageCount,
      userType: isAnonymous ? 'anonymous' : 'registered',
      hasPassword: !!password
    });

    setUploadState('confirming');
    setShowConfirmModal(true);
  }, [isAnonymous, credits]);

  // Handle file selection and show confirmation
  const handleFileSelect = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setSelectedFile(file);
    setError(null);
    uploadStartTime.current = Date.now();

    try {
      // Basic file validation
      if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
        setError('Please select a PDF file.');
        setUploadState('error');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB.');
        setUploadState('error');
        return;
      }

      // 📄 UNIVERSAL PAGE COUNT VALIDATION (ALL USERS)
      // Count pages upfront for accurate credit calculation
      console.log('🔍 Analyzing PDF page count for credit validation...');
      setUploadState('analyzing');

      let pageCount;
      try {
        pageCount = await getPdfPageCount(file);
        console.log(`📄 PDF has ${pageCount} page(s)`);
        setSelectedFilePageCount(pageCount); // Store for modal and transaction
      } catch (pageCountError) {
        // Handle password-protected PDFs - show password modal
        if (pageCountError.name === 'PasswordException') {
          console.log('🔒 Password-protected PDF detected. Showing password prompt...');
          setPendingFile(file); // Store file temporarily
          setShowPasswordModal(true); // Show password modal
          setUploadState('idle'); // Reset state while waiting for password
          return; // Exit here - will continue after password is provided
        }

        // Handle other errors
        console.error('Error checking page count:', pageCountError);
        setError(pageCountError.message || 'Failed to read PDF. Please ensure it is a valid PDF file.');
        setUploadState('error');

        // Track page count error
        trackError({
          errorType: 'page_count_error',
          errorMessage: pageCountError.message,
          component: 'FileUpload',
          additionalContext: {
            fileName: file.name,
            userType: isAnonymous ? 'anonymous' : 'registered'
          }
        });
        return;
      }

      // Continue with validation and confirmation
      await continueFileValidation(file, pageCount);
    } catch (err) {
      console.error('Error validating file:', err);
      setError('Failed to validate file. Please try again.');
      setUploadState('error');

      // Track file validation error
      trackError({
        errorType: 'file_validation_error',
        errorMessage: err.message || 'Failed to validate file',
        component: 'FileUpload',
        additionalContext: {
          fileName: file.name,
          fileSize: file.size
        }
      });
    }
  }, [isAnonymous, credits]);

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop: handleFileSelect,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false,
    disabled: uploadState === 'uploading' || !pdfLibLoaded || (!user && !anonymousUser)
  });

  // Handle file rejections
  useEffect(() => {
    if (fileRejections.length > 0) {
      const rejection = fileRejections[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        setError('File too large. Maximum size is 10MB.');
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        setError('Invalid file type. Only PDF files are supported.');
      } else {
        setError('File upload rejected. Please try again.');
      }
      setUploadState('error');
    }
  }, [fileRejections]);

  // NEW: HTTP Upload Flow (Simplified Architecture)
  const handleConfirmUploadHTTP = useCallback(async (confirmData = {}) => {
    const { year } = confirmData;
    const password = selectedFile?.pdfPassword; // Get password from file if provided upfront

    // Store year for preview modal
    setSelectedYear(year);

    console.log('🚀 HTTP UPLOAD: Starting new HTTP-based upload flow');
    console.log(`🔐 Password protected: ${!!password}`);

    if ((!user && !anonymousUser) || !selectedFile) {
      console.error('❌ HTTP UPLOAD: Missing user or file');
      return;
    }

    // Validate anonymous user has credits
    if (isAnonymous && anonymousCredits === 0) {
      setError('Your free trial credit has been used. Please register to continue.');
      setUploadState('error');
      return;
    }

    setShowConfirmModal(false);
    setUploadState('uploading');
    setUploadProgress(0);

    // Track upload confirmation
    const currentCredits = isAnonymous ? anonymousCredits : credits;
    const creditsRequired = selectedFilePageCount || 1;
    trackPDFUploadConfirmed({
      fileName: selectedFile.name,
      creditsUsed: creditsRequired,
      pageCount: selectedFilePageCount,
      userCreditsRemaining: currentCredits - creditsRequired,
      userType: isAnonymous ? 'anonymous' : 'registered',
      hasPassword: !!password
    });

    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('pageCount', selectedFilePageCount || 1);
      if (year) formData.append('year', year);
      if (password) formData.append('password', password);

      console.log('📤 HTTP UPLOAD: Sending request to processDocument');
      setUploadProgress(10);

      // Get auth token
      const idToken = await auth.currentUser.getIdToken();

      // Call processDocument HTTP endpoint (returns transaction ID immediately)
      const processDocumentURL = getCloudFunctionURL('processDocument');

      const response = await fetch(processDocumentURL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        body: formData
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Processing failed');
      }

      // Set transaction ID and setup real-time progress listener
      setCurrentTransactionId(result.transactionId);
      console.log('👂 HTTP UPLOAD: Starting real-time progress tracking for:', result.transactionId);

      // Listen to transaction document for real-time progress updates
      const transactionUnsubscribe = onSnapshot(
        doc(db, 'transactions', result.transactionId),
        (doc) => {
          if (!isMountedRef.current) return;

          if (doc.exists()) {
            const data = doc.data();

            // Update progress bar from Firestore
            if (data.progress !== undefined) {
              setUploadProgress(data.progress);
              console.log(`📊 HTTP UPLOAD: Progress update: ${data.progress}% - ${data.progressMessage || ''}`);
            }

            // Check if processing complete
            if (data.status === 'awaiting_acceptance' && data.progress === 100) {
              console.log('✅ HTTP UPLOAD: Processing complete, showing preview');
              setPreviewTransactions(data.transactions || []);
              setShowPreviewModal(true);
              setUploadState('awaiting_preview');

              // Clean up listener
              if (transactionUnsubscribe) {
                transactionUnsubscribe();
              }
            }

            // Handle errors
            if (data.status === 'error') {
              console.error('❌ HTTP UPLOAD: Processing failed:', data.errorMessage);
              setError(data.errorMessage || 'Processing failed');
              setUploadState('error');

              // Restore anonymous credits if they were deducted
              // This can happen if error occurs after preview acceptance
              if (isAnonymous && anonymousCredits < 2) {
                const creditsToRestore = selectedFilePageCount || 1;
                const restoredCredits = Math.min(2, anonymousCredits + creditsToRestore);
                console.log(`🔄 Restoring ${creditsToRestore} anonymous credits due to processing error: ${anonymousCredits} → ${restoredCredits}`);
                updateAnonymousCredits(restoredCredits);
              }

              // Clean up listener
              if (transactionUnsubscribe) {
                transactionUnsubscribe();
              }
            }
          }
        },
        (error) => {
          console.error('❌ HTTP UPLOAD: Progress listener error:', error);
        }
      );

      // Store unsubscribe function for cleanup
      transactionListenerRef.current = transactionUnsubscribe;

      // Note: Credits will be deducted AFTER user accepts the preview
      // This prevents the anonymous modal from showing before conversion completes
      if (!isAnonymous) {
        // Registered user credits already deducted by backend
        console.log('💳 HTTP UPLOAD: Refreshing user credits');
        refreshCredits();
      }

    } catch (error) {
      console.error('❌ HTTP UPLOAD: Failed:', error);

      // Parse error from response
      const errorMessage = error.message || 'Upload failed. Please try again.';
      setError(errorMessage);
      setUploadState('error');

      // Track error
      trackPDFConversionError({
        fileName: selectedFile.name,
        errorType: 'http_upload_error',
        errorMessage: errorMessage,
        creditsRefunded: creditsRequired || 0
      });

      // Refresh credits in case of refund
      if (!isAnonymous) {
        refreshCredits();
      }
    }
  }, [user, anonymousUser, selectedFile, selectedFilePageCount, isAnonymous, anonymousCredits, credits, refreshCredits, updateAnonymousCredits]);

  // Direct use of HTTP architecture - Storage-based architecture removed
  const handleConfirmUpload = handleConfirmUploadHTTP;

  // OLD Storage Upload Flow REMOVED (2025-10-27)
  // The legacy Storage-based architecture has been removed to simplify codebase
  // All uploads now use the faster HTTP-based architecture (processDocument endpoint)
  // This removes ~540 lines of complex polling and race-condition handling code

  // Keeping this comment for reference - old function signature was:
  // const handleConfirmUploadStorage = useCallback(async (confirmData = {}) => { ... }, [dependencies]);
  // Function body removed - see git history if needed for reference


  // Handle modal cancellation
  const handleCancelUpload = useCallback(() => {
    // Track upload abandonment
    if (selectedFile) {
      trackUploadAbandonment('confirming', {
        fileName: selectedFile.name,
        creditsRequired: selectedFilePageCount || 1,
        pageCount: selectedFilePageCount
      });
    }

    setShowConfirmModal(false);
    setUploadState('idle');
  }, [selectedFile, selectedFilePageCount]);

  // Handle retry after error
  const handleRetry = useCallback(() => {
    resetForm();
  }, [resetForm]);

  // Cancel ongoing upload
  const handleCancelOngoingUpload = useCallback(() => {
    if (uploadTaskRef.current) {
      uploadTaskRef.current.cancel();
      uploadTaskRef.current = null;
    }

    // Update transaction status to cancelled
    if (currentTransactionId) {
      updateTransactionStatus(currentTransactionId, TRANSACTION_STATUS.ERROR, {
        errorMessage: 'Upload cancelled by user',
        uploadCancelled: new Date(),
        cancelledByUser: true // Flag for backend to trigger immediate refund
      });
    }

    // Refresh credits for registered users to reflect cancellation refund
    // Backend monitors cancelledByUser flag and processes refund
    if (!isAnonymous && user?.uid) {
      console.log('🔄 User cancelled upload. Refreshing credits after backend processes cancellation...');
      setTimeout(() => {
        refreshCredits();
      }, 2000); // Wait 2 seconds for backend transaction monitor to process refund
    }

    resetForm();
  }, [currentTransactionId, updateTransactionStatus, resetForm, isAnonymous, user, refreshCredits]);

  // Handle preview acceptance
  const handleAcceptPreview = useCallback(async (editedTransactions) => {
    if (!currentTransactionId) {
      console.error('❌ No transaction ID available');
      const errorMsg = 'Transaction ID not found. Please try again.';
      setError(errorMsg);
      throw new Error(errorMsg); // Throw to prevent modal from closing
    }

    try {
      // HTTP Architecture - old Storage code removed
        // NEW ARCHITECTURE: Accept has already been handled by PreviewModal
        // Just close modal and show success
        console.log('✅ NEW: Preview accepted by backend, showing success');

        setShowPreviewModal(false);
        setUploadState('complete');

        // Deduct anonymous credits NOW that conversion is complete and accepted
        if (isAnonymous) {
          const creditsRequired = selectedFilePageCount || 1;
          const newCredits = Math.max(0, anonymousCredits - creditsRequired);
          console.log(`💳 COMPLETION: Deducting ${creditsRequired} anonymous credit(s). ${anonymousCredits} → ${newCredits}`);
          updateAnonymousCredits(newCredits);
        }

        // Track success
        const processingTime = Math.floor((Date.now() - uploadStartTime.current) / 1000);
        trackPDFConversionSuccess({
          fileName: selectedFile.name,
          creditsUsed: selectedFilePageCount || 1,
          processingTimeSeconds: processingTime,
          outputFormat: 'pending', // Downloads happen from transaction history
          transactionId: currentTransactionId,
          userType: isAnonymous ? 'anonymous' : 'registered'
        });

        return; // Exit early for new architecture
    } catch (error) {
      console.error('❌ Failed to accept preview:', error);

      // Parse error message from Firebase Functions
      let errorMessage = 'Failed to process preview. Please try again.';
      if (error.code === 'functions/unauthenticated') {
        errorMessage = 'Authentication expired. Please refresh and try again.';
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'Permission denied. Please try again or contact support.';
      } else if (error.code === 'functions/failed-precondition') {
        errorMessage = error.message || 'Transaction is not in the correct state.';
      } else if (error.code === 'functions/invalid-argument') {
        errorMessage = error.message || 'Invalid transaction data.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      setUploadState('error');
      setShowPreviewModal(false);

      // Track preview acceptance error
      trackError({
        errorType: 'preview_acceptance_error',
        errorMessage: errorMessage,
        errorCode: error.code || 'unknown',
        component: 'FileUpload',
        additionalContext: {
          transactionId: currentTransactionId,
          transactionCount: editedTransactions.length
        }
      });

      // Re-throw error to keep modal open if needed
      throw new Error(errorMessage);
    }
  }, [currentTransactionId]);

  // Note: Preview modal no longer has a close button
  // Credits are deducted upfront, so users must complete the transaction by accepting
  // This prevents accidental credit loss from closing the modal

  // Render different states
  const renderUploadArea = () => {
    if (!pdfLibLoaded) {
      return (
        <div className="text-center py-8">
          <ArrowPathIcon className="h-12 w-12 text-blue-500 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading PDF Library...</h3>
          <p className="text-gray-600">Preparing file validator</p>
        </div>
      );
    }

    if (uploadState === 'uploading') {
      return (
        <div className="text-center py-8">
          <CloudArrowUpIcon className="h-12 w-12 text-blue-500 mx-auto mb-4 animate-pulse" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Uploading...</h3>
          <p className="text-gray-600 mb-4">{selectedFile?.name}</p>
          
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div 
              className="bg-blue-500 h-3 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-sm text-gray-500 mb-4">{uploadProgress}% complete</p>
          
          <button
            onClick={handleCancelOngoingUpload}
            className="text-red-600 hover:text-red-800 text-sm font-medium"
          >
            Cancel Upload
          </button>
        </div>
      );
    }

    if (uploadState === 'complete') {
      return (
        <div className="text-center py-8">
          <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isAnonymous ? 'CSV Downloaded!' : 'Conversion Complete!'}
          </h3>
          <p className="text-gray-600 mb-4">
            {isAnonymous
              ? 'Your CSV has been downloaded. Check transaction history below for additional formats (OFX, QBO).'
              : 'Your file has been processed successfully. Check transaction history below to download.'}
          </p>

          {isAnonymous && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4 max-w-md mx-auto">
              <p className="text-xs text-orange-800">
                ⚠️ <strong>24-hour access:</strong> Your files will be automatically deleted after 24 hours for privacy. Register to keep files for 30 days.
              </p>
            </div>
          )}

          <button
            onClick={resetForm}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            Upload Another File
          </button>
        </div>
      );
    }

    if (uploadState === 'error') {
      return (
        <div className="text-center py-8">
          <ExclamationCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Failed</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleRetry}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            Try Again
          </button>
        </div>
      );
    }

    if (uploadState === 'analyzing') {
      return (
        <div className="text-center py-8">
          <ArrowPathIcon className="h-12 w-12 text-blue-500 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Analyzing File...</h3>
          <p className="text-gray-600">Calculating credits required</p>
        </div>
      );
    }

    // Default idle state
    return (
      <div 
        {...getRootProps()} 
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors duration-200
          ${isDragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }
          ${!user ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        
        <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />

        {!user && !anonymousUser ? (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Please log in to upload files</h3>
            <p className="text-gray-600">You need to be logged in or try for free to convert bank statements.</p>
          </>
        ) : isDragActive ? (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Drop your PDF here</h3>
            <p className="text-gray-600">Release to upload your bank statement</p>
          </>
        ) : (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Bank Statement</h3>
            <p className="text-gray-600 mb-4">
              Drag and drop your PDF bank statement here, or click to browse
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
              <span>• PDF files only</span>
              <span>• Max 10MB</span>
              {isAnonymous ? (
                <span className="text-orange-600 font-medium">• 2 pages max (free trial)</span>
              ) : (
                <span>• 1 Credit per page</span>
              )}
            </div>
            {isAnonymous && (
              <p className="text-xs text-orange-600 mt-2">
                Free trial limited to 2-page PDFs. Register to convert multi-page documents.
              </p>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Credit balance display */}
      {(user || anonymousUser) && (
        <div className={`mb-6 p-4 ${isAnonymous ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'} border rounded-lg`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CurrencyDollarIcon className={`h-5 w-5 ${isAnonymous ? 'text-orange-600' : 'text-green-600'} mr-2`} />
              <span className={`${isAnonymous ? 'text-orange-800' : 'text-green-800'} font-medium`}>
                {isAnonymous
                  ? `Free Trial Credits: ${anonymousCredits || 0}`
                  : `Available Credits: ${credits || 0}`}
              </span>
            </div>
            {!isAnonymous && (credits || 0) < 5 && (
              <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                Buy More Credits
              </button>
            )}
            {isAnonymous && anonymousCredits === 0 && (
              <Link to="/register">
                <button className="text-green-600 hover:text-green-800 text-sm font-medium">
                  Register to Continue
                </button>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Main upload area */}
      {renderUploadArea()}

      {/* Password prompt modal */}
      {showPasswordModal && selectedFile && (
        <PasswordPromptModal
          isOpen={showPasswordModal}
          onClose={() => {
            setShowPasswordModal(false);
            setPendingFile(null);
            setSelectedFile(null);
            setUploadState('idle');
          }}
          fileName={selectedFile?.name}
          onSubmit={handlePasswordSubmit}
        />
      )}

      {/* Credit confirmation modal */}
      {showConfirmModal && selectedFile && (
        <CreditConfirmModal
          isOpen={showConfirmModal}
          onClose={handleCancelUpload}
          onConfirm={handleConfirmUpload}
          fileName={selectedFile?.name}
          pageCount={selectedFilePageCount || 1}
          userCredits={isAnonymous ? (anonymousCredits || 0) : (credits || 0)}
        />
      )}

      {/* Preview modal */}
      {showPreviewModal && previewTransactions.length > 0 && (
        <PreviewModal
          isOpen={showPreviewModal}
          transactions={previewTransactions}
          fileName={selectedFile?.name || 'bank_statement.pdf'}
          transactionId={currentTransactionId}
          statementYear={selectedYear}
          onAccept={handleAcceptPreview}
        />
      )}
    </div>
  );
};

export default FileUpload;
